import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { DEFAULT_BRANDING, type PortalBranding, usePortalBranding } from '@/api';
import { applyBrandColor } from '@/lib/brand';

const BrandContext = createContext<PortalBranding>(DEFAULT_BRANDING);

/**
 * Loads `GET /v1/tenant/branding` once per session and applies it: the primary color becomes
 * the `--color-brand-*` tokens on `<html>`, the portal name becomes the document title. Renders
 * children immediately with the defaults so the shell never waits on branding.
 */
export function BrandProvider({ children }: { children: ReactNode }) {
  const { branding } = usePortalBranding();

  useEffect(() => {
    applyBrandColor(document.documentElement, branding.primaryColor);
  }, [branding.primaryColor]);

  return <BrandContext.Provider value={branding}>{children}</BrandContext.Provider>;
}

export function useBrand(): PortalBranding {
  return useContext(BrandContext);
}

/** `document.title = "<page> - <portal name>"`; the portal name alone when no page title. */
export function usePageTitle(title?: string | null): void {
  const brand = useBrand();
  useEffect(() => {
    document.title = title ? `${title} - ${brand.portalName}` : brand.portalName;
  }, [title, brand.portalName]);
}
